import { UserRole } from "constants/userRole";
import { useUserWithAccount } from "context/UserAccountProvider";
import { centsToDollars, parsePrice } from "helpers/price";
import { Button } from "primereact/button";
import { ReactNode, useEffect, useMemo, useState } from "react";
import {
  useGetPlineReconAccountsQuery,
  useLazyGetPlineReconciliationQuery,
  useLazyGetPlineReconciliationWorkbookQuery,
} from "store/queries/plineReconciliation";
import styled from "styled-components";
import {
  IPlineCycle,
  IPlineExplanation,
  IPlineReconciliation,
  IPlineReconciliationArgs,
} from "types/PlineReconciliation";

// CLE-2917: finance's P-line / GL reconciliation for one P-line facility over a range of report dates.
// SuperAdmin only. Nothing loads until "Run reconciliation" is clicked.

export const formatMoney = (cents: number | null | undefined): string => {
  if (cents === null || cents === undefined) return "";
  const text = parsePrice(centsToDollars(Math.abs(cents)));
  return cents < 0 ? `(${text})` : text;
};

const isoDay = (d: Date) => d.toISOString().slice(0, 10);

export const defaultRange = (now: Date = new Date()) => {
  const from = new Date(now.getTime());
  from.setUTCDate(from.getUTCDate() - 90);
  return { from: isoDay(from), to: isoDay(now) };
};

// Plain-English titles for the API's explanation codes (see PLINE-RECONCILIATION.md). The raw code is still
// shown next to the title, so an unknown code falls back to itself rather than disappearing.
export const EXPLANATION_LABELS: Record<string, string> = {
  NO_PAYOUT_LINKED: "No payout linked to this week",
  PAYOUT_NOT_PAID: "Payout not paid yet",
  PAYOUT_AMOUNT_UNKNOWN: "Payout amount unknown",
  PAYOUT_COMPOSITION_NOT_CAPTURED: "Payout lines were not captured",
  CASH_DEBIT_NOT_IN_THIS_PAYOUT: "Cash debit is in another week's payout",
  CASH_DEBIT_FROM_OTHER_CYCLE: "Payout carries another week's cash debit",
  PLINE_ROW_WITHOUT_PAYOUT_LINE: "P-line rows with no payout line",
  PAYOUT_LINE_WITHOUT_PLINE_ROW: "Payout lines with no P-line row",
  CASH_ROWS_DIFFER_FROM_PAYMENTS: "Cash rows differ from cash payments",
  REFUND_IN_CYCLE: "Refunds in this week",
  GL_OMITS_NEGATIVE_MONTH: "GL file omits a negative month",
  OFFSET_BY_ADJACENT_CYCLE: "Offset by the adjacent week (timing)",
  ROUNDING: "Rounding",
  UNEXPLAINED: "Unexplained remainder",
};

export const explanationLabel = (code: string) =>
  EXPLANATION_LABELS[code] ?? code;

type Tone = "ok" | "bad" | "warn" | undefined;

const StyledContainer = styled.div`
  --pr-border: #e5e7eb;
  --pr-border-strong: #d1d5db;
  --pr-muted: #4b5563;
  --pr-subtle-bg: #f9fafb;
  --pr-head-bg: #f3f4f6;
  --pr-ok: #166534;
  --pr-ok-bg: #e3f4e1;
  --pr-bad: #991b1b;
  --pr-bad-bg: #fde2e1;
  --pr-warn: #b45309;
  --pr-warn-bg: #fffbeb;
  --pr-warn-border: #fcd34d;
  --pr-mono: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;

  min-width: 0;
  max-width: 100%;

  .muted {
    color: var(--pr-muted);
  }

  .helper {
    color: var(--pr-muted);
    font-size: 0.875rem;
    margin: 0.25rem 0 0;
  }

  .mono {
    font-family: var(--pr-mono);
    font-size: 0.8125rem;
  }

  .controls label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 0.85rem;
  }

  .form-message {
    color: var(--pr-bad);
    font-size: 0.875rem;
    margin: 0.5rem 0 0;
  }

  /* ---- result header ---- */
  .results {
    margin-top: 1.5rem;
  }

  .results-header {
    border: 1px solid var(--pr-border);
    border-radius: 10px;
    background: var(--pr-subtle-bg);
    padding: 1rem 1.25rem;
  }

  .results-header h4 {
    margin: 0;
    font-size: 1.125rem;
  }

  .results-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 0.75rem;
    margin-top: 0.25rem;
    color: var(--pr-muted);
    font-size: 0.875rem;
  }

  .stale {
    margin-top: 0.75rem;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    background: var(--pr-warn-bg);
    border: 1px solid var(--pr-warn-border);
    color: #78350f;
    font-size: 0.875rem;
  }

  .toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin: 1.5rem 0 0.75rem;
  }

  .toolbar h4 {
    margin: 0;
    font-size: 1rem;
  }

  /* ---- stat tiles ---- */
  .tiles {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 0.75rem;
    margin-top: 1rem;
  }

  .tile {
    background: #fff;
    border: 1px solid var(--pr-border);
    border-radius: 8px;
    padding: 0.625rem 0.875rem;
    min-width: 0;
  }

  .tile-label {
    color: var(--pr-muted);
    font-size: 0.8125rem;
  }

  .tile-value {
    margin-top: 0.125rem;
    font-size: 1.125rem;
    font-weight: 600;
    text-align: right;
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
  }

  .tile.tone-ok {
    background: var(--pr-ok-bg);
    border-color: #b7dfb3;
  }
  .tile.tone-ok .tile-value {
    color: var(--pr-ok);
  }
  .tile.tone-bad {
    background: var(--pr-bad-bg);
    border-color: #f6b4b1;
  }
  .tile.tone-bad .tile-value {
    color: var(--pr-bad);
  }
  .tile.tone-warn {
    background: var(--pr-warn-bg);
    border-color: var(--pr-warn-border);
  }
  .tile.tone-warn .tile-value {
    color: var(--pr-warn);
  }

  /* ---- badges ---- */
  .pill {
    display: inline-block;
    border-radius: 999px;
    padding: 2px 10px;
    font-size: 0.8125rem;
    font-weight: 600;
    white-space: nowrap;
  }

  .balanced {
    color: var(--pr-ok);
    background: var(--pr-ok-bg);
  }

  .unbalanced {
    color: var(--pr-bad);
    background: var(--pr-bad-bg);
  }

  /* ---- cycle cards ---- */
  .cycle {
    background: #fff;
    border: 1px solid var(--pr-border);
    border-radius: 10px;
    padding: 1rem 1.25rem;
    margin-top: 1rem;
  }

  .cycle.cycle-unbalanced {
    border-left: 4px solid var(--pr-warn);
  }

  .cycle-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .cycle-header h4 {
    margin: 0;
    font-size: 1.0625rem;
  }

  .cycle-toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: none;
    border: 0;
    padding: 0.25rem 0.375rem;
    margin-left: -0.375rem;
    border-radius: 6px;
    font: inherit;
    font-weight: 600;
    color: inherit;
    cursor: pointer;
  }

  .cycle-toggle:hover {
    background: var(--pr-head-bg);
  }

  .cycle-toggle:focus-visible,
  .copy-btn:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }

  .cycle-toggle .pi {
    font-size: 0.8rem;
    color: var(--pr-muted);
  }

  .cycle-body {
    margin-top: 1.25rem;
  }

  /* ---- explanations callout ---- */
  .callout {
    background: var(--pr-warn-bg);
    border: 1px solid var(--pr-warn-border);
    border-radius: 8px;
    padding: 0.875rem 1rem;
    margin-bottom: 1.25rem;
  }

  .callout h5 {
    margin: 0 0 0.5rem;
    font-size: 0.9375rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .explanations {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .explanation {
    padding: 0.625rem 0;
  }

  .explanation + .explanation {
    border-top: 1px solid var(--pr-warn-border);
  }

  .explanation-head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.25rem 0.75rem;
  }

  .explanation-title {
    font-weight: 600;
  }

  .explanation-code {
    color: var(--pr-muted);
    font-size: 0.75rem;
  }

  .explanation-effect {
    margin-left: auto;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    text-align: right;
  }

  .explanation-effect.context {
    font-weight: 400;
    color: var(--pr-muted);
    font-size: 0.8125rem;
  }

  .explanation-message {
    margin: 0.25rem 0 0;
    color: #374151;
    font-size: 0.875rem;
    line-height: 1.45;
  }

  .explanation-orders {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.25rem 0.75rem;
    margin-top: 0.375rem;
    font-size: 0.8125rem;
  }

  /* ---- sub-sections ---- */
  .sub {
    min-width: 0;
  }

  .sub + .sub,
  .sub-grid + .sub,
  .sub + .sub-grid {
    border-top: 1px solid var(--pr-border);
    margin-top: 1.25rem;
    padding-top: 1.25rem;
  }

  .sub-grid > .sub + .sub {
    border-top: 0;
    margin-top: 0;
    padding-top: 0;
  }

  .sub h5 {
    margin: 0 0 0.5rem;
    font-size: 0.9375rem;
  }

  .sub h5 .count {
    color: var(--pr-muted);
    font-weight: 400;
    font-size: 0.8125rem;
    margin-left: 0.5rem;
  }

  .sub-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 1.25rem;
  }

  @media (min-width: 1100px) {
    .sub-grid {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      gap: 2rem;
    }
  }

  .empty {
    color: var(--pr-muted);
    font-size: 0.875rem;
    font-style: italic;
  }

  /* ---- tables ---- */
  .table-scroll {
    max-width: 100%;
    overflow-x: auto;
    border: 1px solid var(--pr-border);
    border-radius: 8px;
  }

  table {
    border-collapse: collapse;
    font-size: 0.85rem;
    width: 100%;
  }

  th,
  td {
    padding: 0.5rem 0.75rem;
    text-align: left;
    vertical-align: top;
  }

  thead th {
    background: var(--pr-head-bg);
    color: #374151;
    font-weight: 600;
    font-size: 0.8125rem;
    white-space: nowrap;
    border-bottom: 1px solid var(--pr-border-strong);
  }

  tbody tr + tr td {
    border-top: 1px solid var(--pr-border);
  }

  tfoot td {
    border-top: 2px solid var(--pr-border-strong);
    background: var(--pr-subtle-bg);
    font-weight: 700;
  }

  td.money,
  th.money {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  td.nowrap {
    white-space: nowrap;
  }

  td.wrap {
    min-width: 16rem;
  }

  /* In the wide P-line table an id keeps one line; the table scrolls inside its own box instead. */
  td.id-cell {
    white-space: nowrap;
  }
  td.id-cell .id-text {
    overflow-wrap: normal;
  }

  /* ---- ids ---- */
  .id {
    display: inline-flex;
    align-items: flex-start;
    gap: 0.25rem;
    max-width: 100%;
    min-width: 0;
  }

  .id-text {
    font-family: var(--pr-mono);
    font-size: 0.8125rem;
    overflow-wrap: anywhere;
    background: none;
    padding: 0;
    color: inherit;
  }

  .copy-btn {
    flex: none;
    background: none;
    border: 0;
    padding: 0 0.125rem;
    color: var(--pr-muted);
    cursor: pointer;
    border-radius: 4px;
    line-height: 1.2;
  }

  .copy-btn:hover {
    color: #1f2937;
  }

  .copy-btn .pi {
    font-size: 0.75rem;
  }
`;

// A full id in monospace that wraps instead of widening the layout, with a copy button. The value is never
// shortened in the DOM, so selecting and copying the text gives the whole id too.
function CopyableId({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Clipboard can be unavailable (insecure context); the text itself is still selectable.
    }
  };
  return (
    <span className="id">
      <code className="id-text" title={value}>
        {value}
      </code>
      <button
        type="button"
        className="copy-btn"
        aria-label={`Copy ${value}`}
        title={copied ? "Copied" : "Copy"}
        onClick={copy}
      >
        <i
          className={`pi ${copied ? "pi-check" : "pi-copy"}`}
          aria-hidden="true"
        />
      </button>
    </span>
  );
}

function Stat({
  label,
  value,
  tone,
  testId,
}: {
  label: string;
  value: ReactNode;
  tone?: Tone;
  testId?: string;
}) {
  return (
    <div className={`tile${tone ? ` tone-${tone}` : ""}`}>
      <div className="tile-label">{label}</div>
      <div className="tile-value" data-testid={testId}>
        {value}
      </div>
    </div>
  );
}

function Explanation({ e }: { e: IPlineExplanation }) {
  return (
    <li className="explanation" data-testid="pline-explanation">
      <div className="explanation-head">
        <span className="explanation-title">{explanationLabel(e.code)}</span>
        <code
          className="explanation-code mono"
          title="Explanation code (as in the workbook)"
        >
          {e.code}
        </code>
        {e.effectInCents !== null ? (
          <span className="explanation-effect">
            {formatMoney(e.effectInCents)}
          </span>
        ) : (
          <span
            className="explanation-effect context"
            title="Context only — this does not account for any of the balance"
          >
            Context
          </span>
        )}
      </div>
      <p className="explanation-message">{e.message}</p>
      {e.orderIds.length > 0 && (
        <div className="explanation-orders">
          <span className="muted">
            {e.orderIds.length === 1 ? "Order" : "Orders"}
          </span>
          {e.orderIds.map((id) => (
            <CopyableId key={id} value={id} />
          ))}
        </div>
      )}
    </li>
  );
}

function CycleCard({
  cycle,
  open,
  onToggle,
}: {
  cycle: IPlineCycle;
  open: boolean;
  onToggle: () => void;
}) {
  const bodyId = `pline-cycle-body-${cycle.reportDate}`;
  return (
    <section
      className={`cycle${cycle.balanced ? "" : " cycle-unbalanced"}`}
      data-testid={`pline-cycle-${cycle.reportDate}`}
      aria-label={`Cycle ${cycle.reportDate}`}
    >
      <div className="cycle-header">
        <h4>
          <button
            type="button"
            className="cycle-toggle"
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={onToggle}
          >
            <i
              className={`pi ${open ? "pi-chevron-down" : "pi-chevron-right"}`}
              aria-hidden="true"
            />
            Report date {cycle.reportDate}
          </button>
        </h4>
        <span
          className={`pill ${cycle.balanced ? "balanced" : "unbalanced"}`}
          data-testid="cycle-balance"
        >
          {cycle.balanced
            ? "Balanced"
            : `Out of balance: ${formatMoney(cycle.balanceInCents)}`}
        </span>
      </div>

      <div className="tiles">
        <Stat
          label="P-line total"
          value={formatMoney(cycle.plineTotalInCents)}
        />
        <Stat
          label="Cash paid at facility"
          value={formatMoney(cycle.cashAtFacilityInCents)}
        />
        <Stat
          label="Payouts from Clear"
          value={formatMoney(cycle.payoutTotalInCents)}
        />
        <Stat
          label="Balance"
          value={formatMoney(cycle.balanceInCents)}
          tone={cycle.balanced ? "ok" : "bad"}
        />
        <Stat
          label="Cumulative balance"
          value={formatMoney(cycle.cumulativeBalanceInCents)}
          tone={cycle.cumulativeBalanceInCents === 0 ? undefined : "warn"}
        />
      </div>

      {open && (
        <div className="cycle-body" id={bodyId}>
          {cycle.explanations.length > 0 && (
            <div className="callout">
              <h5>
                <i
                  className="pi pi-exclamation-triangle"
                  aria-hidden="true"
                  style={{ color: "var(--pr-warn)" }}
                />
                Why this cycle is out of balance
              </h5>
              <ul className="explanations">
                {cycle.explanations.map((e) => (
                  <Explanation key={`${e.code}-${e.message}`} e={e} />
                ))}
              </ul>
            </div>
          )}

          <section className="sub" aria-label="P-line submissions">
            <h5>
              P-line submissions
              <span className="count">
                {cycle.plineRows.length}{" "}
                {cycle.plineRows.length === 1 ? "row" : "rows"}
              </span>
            </h5>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Unit number</th>
                    <th>Procedure code</th>
                    <th className="money">Amount</th>
                    <th>Date</th>
                    <th>SN comment</th>
                    <th>Order ID</th>
                  </tr>
                </thead>
                <tbody>
                  {cycle.plineRows.map((r, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={`${r.reportId}-${i}`}>
                      <td className="mono nowrap">{r.unitNumber}</td>
                      <td className="mono nowrap">{r.procedureCode}</td>
                      <td className="money">{formatMoney(r.amountInCents)}</td>
                      <td className="nowrap">{r.date}</td>
                      <td className="wrap">{r.snComment}</td>
                      <td className="id-cell">
                        {r.orderId ? <CopyableId value={r.orderId} /> : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr data-testid="pline-ar-total">
                    <td colSpan={2}>AR Total</td>
                    <td className="money">
                      {formatMoney(cycle.plineTotalInCents)}
                    </td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          <div className="sub-grid">
            <section className="sub" aria-label="Cash paid at facility">
              <h5>Cash paid at facility</h5>
              {cycle.cash.length === 0 ? (
                <div className="empty">None</div>
              ) : (
                <div className="table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Status</th>
                        <th className="money">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cycle.cash.map((c) => (
                        <tr key={c.paymentId}>
                          <td>
                            <CopyableId value={c.orderId} />
                          </td>
                          <td>{c.status}</td>
                          <td className="money">
                            {formatMoney(c.amountInCents)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={2}>Total</td>
                        <td className="money">
                          {formatMoney(cycle.cashAtFacilityInCents)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </section>
            <section className="sub" aria-label="Payouts from Clear">
              <h5>Payouts from Clear</h5>
              {cycle.payouts.length === 0 ? (
                <div className="empty">No payout linked</div>
              ) : (
                <div className="table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Payout</th>
                        <th>Arrival date</th>
                        <th>Status</th>
                        <th className="money">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cycle.payouts.map((p) => (
                        <tr key={p.id}>
                          <td>
                            <CopyableId value={p.externalId} />
                          </td>
                          <td className="nowrap">{p.arrivalDate ?? ""}</td>
                          <td>{p.status}</td>
                          <td className="money">
                            {formatMoney(p.amountInCents)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3}>Total</td>
                        <td className="money">
                          {formatMoney(cycle.payoutTotalInCents)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </section>
          </div>

          <section className="sub" aria-label="GL submissions">
            <h5>GL submissions</h5>
            {cycle.glRows.length === 0 ? (
              <div className="empty">No GL rows</div>
            ) : (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>COID</th>
                      <th>Account</th>
                      <th>Description</th>
                      <th className="money">Debit</th>
                      <th className="money">Credit</th>
                      <th>Period end</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cycle.glRows.map((g) => (
                      <tr key={`${g.reportId}-${g.account}-${g.periodEnd}`}>
                        <td className="mono nowrap">{g.coid}</td>
                        <td className="mono nowrap">{g.account}</td>
                        <td>{g.description}</td>
                        <td className="money">{formatMoney(g.debitInCents)}</td>
                        <td className="money">
                          {formatMoney(g.creditInCents)}
                        </td>
                        <td className="mono nowrap">{g.periodEnd}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3}>Total</td>
                      <td className="money">
                        {formatMoney(cycle.glDebitInCents)}
                      </td>
                      <td className="money">
                        {formatMoney(cycle.glCreditInCents)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
    </section>
  );
}

function Results({
  recon,
  stale,
}: {
  recon: IPlineReconciliation;
  stale: boolean;
}) {
  // Out-of-balance weeks open, balanced weeks collapsed.
  const [open, setOpen] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(recon.cycles.map((c) => [c.reportDate, !c.balanced]))
  );
  const setAll = (value: boolean) =>
    setOpen(Object.fromEntries(recon.cycles.map((c) => [c.reportDate, value])));
  const allOpen = recon.cycles.every((c) => open[c.reportDate]);
  const allClosed = recon.cycles.every((c) => !open[c.reportDate]);
  const totalBalanced = recon.totals.balanceInCents === 0;

  return (
    <div className="results" data-testid="pline-reconciliation-results">
      <div className="results-header">
        <h4 data-testid="pline-showing">
          Showing {recon.account.name}, {recon.from} – {recon.to}
        </h4>
        <div className="results-meta">
          <span>
            {recon.cycleCount} weekly{" "}
            {recon.cycleCount === 1 ? "cycle" : "cycles"}
          </span>
          <span
            className={`pill ${
              recon.unbalancedCycleCount === 0 ? "balanced" : "unbalanced"
            }`}
          >
            {recon.unbalancedCycleCount} out of balance
          </span>
        </div>
        {stale && (
          <div className="stale" role="status" data-testid="pline-stale">
            Inputs changed — run again to update. The result below is still for{" "}
            {recon.account.name}, {recon.from} – {recon.to}.
          </div>
        )}
        <div className="tiles">
          <Stat
            label="P-line total"
            value={formatMoney(recon.totals.plineTotalInCents)}
          />
          <Stat
            label="Cash paid at facility"
            value={formatMoney(recon.totals.cashAtFacilityInCents)}
          />
          <Stat
            label="Payouts from Clear"
            value={formatMoney(recon.totals.payoutTotalInCents)}
          />
          <Stat
            label="Balance"
            value={formatMoney(recon.totals.balanceInCents)}
            tone={totalBalanced ? "ok" : "bad"}
            testId="total-balance"
          />
          <Stat
            label="GL debit / credit"
            value={`${formatMoney(recon.totals.glDebitInCents)} / ${formatMoney(
              recon.totals.glCreditInCents
            )}`}
          />
        </div>
        <p className="helper mt-3">
          Balance = P-line total − Cash paid at facility − Payouts from Clear. A
          week is balanced when this is zero.
        </p>
        {recon.notes.map((n) => (
          <p key={n} className="helper">
            {n}
          </p>
        ))}
      </div>

      {recon.cycles.length === 0 ? (
        <p className="muted mt-3">No P-line reports are dated in this range.</p>
      ) : (
        <>
          <div className="toolbar">
            <h4>Weekly cycles</h4>
            <div className="flex gap-2">
              <Button
                label="Expand all"
                icon="pi pi-angle-double-down"
                className="p-button-text p-button-sm"
                disabled={allOpen}
                onClick={() => setAll(true)}
              />
              <Button
                label="Collapse all"
                icon="pi pi-angle-double-up"
                className="p-button-text p-button-sm"
                disabled={allClosed}
                onClick={() => setAll(false)}
              />
            </div>
          </div>
          {recon.cycles.map((c) => (
            <CycleCard
              key={c.reportDate}
              cycle={c}
              open={Boolean(open[c.reportDate])}
              onToggle={() =>
                setOpen((prev) => ({
                  ...prev,
                  [c.reportDate]: !prev[c.reportDate],
                }))
              }
            />
          ))}
        </>
      )}
    </div>
  );
}

export function PlineReconciliationPanel() {
  const { data: accountsResponse, isLoading: accountsLoading } =
    useGetPlineReconAccountsQuery();
  const [run, { data, isFetching, error }] =
    useLazyGetPlineReconciliationQuery();
  const [fetchWorkbook, { isFetching: downloading }] =
    useLazyGetPlineReconciliationWorkbookQuery();
  const initial = useMemo(() => defaultRange(), []);
  const [accountId, setAccountId] = useState("");
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // The LPNT / ScionHealth aggregate accounts have no payouts of their own; reconcile the facility instead.
  const accounts = (accountsResponse?.data ?? []).filter((a) => !a.isAggregate);
  const args: IPlineReconciliationArgs = { accountId, from, to };
  const rangeReversed = Boolean(from && to && from > to);
  const ready = Boolean(accountId && from && to && !rangeReversed);
  // A failed run must not leave the previous result on screen as if it were the answer.
  const recon = error ? undefined : data?.data;
  // The result stays on screen when the form is edited, but says it no longer matches the inputs.
  const stale = Boolean(
    recon &&
      (recon.account.id !== accountId || recon.from !== from || recon.to !== to)
  );

  const downloadExcel = async () => {
    if (!recon) return;
    setDownloadError(null);
    try {
      // The workbook is the reconciliation on screen — its own account and range, whatever the form says now.
      const { blob, fileName } = await fetchWorkbook({
        accountId: recon.account.id,
        from: recon.from,
        to: recon.to,
      }).unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setDownloadError(e?.data?.message ?? "The workbook could not be built.");
    }
  };

  return (
    <StyledContainer data-testid="pline-reconciliation">
      <h3 className="mt-0 mb-2">P-line / GL reconciliation</h3>
      <p className="mt-0 mb-3 muted">
        For a P-line facility, each weekly report&apos;s P-line submissions, the
        cash collected at the facility, the payouts from Clear and the GL lines,
        with the balance per week and why it is not zero.
      </p>
      <div className="controls flex flex-wrap align-items-end gap-3">
        <label htmlFor="pline-recon-account">
          Facility
          <select
            id="pline-recon-account"
            className="p-inputtext"
            value={accountId}
            disabled={accountsLoading}
            onChange={(e) => setAccountId(e.target.value)}
          >
            <option value="">Select a P-line facility</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.firstReportDate} to {a.lastReportDate})
              </option>
            ))}
          </select>
        </label>
        <label htmlFor="pline-recon-from">
          Report dates from
          <input
            id="pline-recon-from"
            type="date"
            className={`p-inputtext${rangeReversed ? " p-invalid" : ""}`}
            value={from}
            aria-invalid={rangeReversed}
            aria-describedby={
              rangeReversed ? "pline-recon-range-error" : undefined
            }
            onChange={(e) => setFrom(e.target.value)}
          />
        </label>
        <label htmlFor="pline-recon-to">
          to
          <input
            id="pline-recon-to"
            type="date"
            className={`p-inputtext${rangeReversed ? " p-invalid" : ""}`}
            value={to}
            aria-invalid={rangeReversed}
            aria-describedby={
              rangeReversed ? "pline-recon-range-error" : undefined
            }
            onChange={(e) => setTo(e.target.value)}
          />
        </label>
        <Button
          label="Run reconciliation"
          disabled={!ready}
          loading={isFetching}
          onClick={() => run(args)}
        />
        {recon && (
          <Button
            label="Download Excel"
            icon="pi pi-download"
            className="p-button-outlined"
            loading={downloading}
            onClick={downloadExcel}
          />
        )}
      </div>
      {rangeReversed && (
        <p
          id="pline-recon-range-error"
          className="form-message"
          data-testid="pline-range-error"
        >
          The start date ({from}) is after the end date ({to}). Pick a start
          date on or before the end date to run the reconciliation.
        </p>
      )}
      {error && (
        <p className="form-message" role="alert">
          {(error as any)?.data?.message ??
            "The reconciliation could not be built."}
        </p>
      )}
      {downloadError && (
        <p className="form-message" role="alert">
          {downloadError}
        </p>
      )}
      {recon && (
        <Results
          // A new run (a different result) resets which weeks are expanded.
          key={`${recon.account.id}|${recon.from}|${recon.to}|${recon.cycles
            .map((c) => c.reportDate)
            .join(",")}`}
          recon={recon}
          stale={stale}
        />
      )}
    </StyledContainer>
  );
}

export function PlineReconciliation() {
  const { userInfo } = useUserWithAccount();
  if (userInfo?.scope !== UserRole.SUPER_ADMIN) return null;
  return <PlineReconciliationPanel />;
}
